import { Loader } from 'google-maps';
import MainService from './main-service';
import ScreenService from './screen-service';
import { EXTENSION_FORMAT_PNG } from '../constants/system/extension-formats';
import { HTML_TAG_NAME_IMG } from '../constants/system/html-tag-names';

export default class GoogleMapService {
  public static GOOGLE_MAPS_LIBRARY_PLACES: string = 'places';
  public static GOOGLE_MAPS_LIBRARY_MARKER: string = 'marker';
  public static GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_STREET: string = 'route';
  public static GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_HOUSE: string = 'street_number';
  public static GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_LOCALITY: string = 'locality';
  public static GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_SUBLOCALITY: string = 'sublocality';
  public static GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_STATE: string = 'administrative_area_level_1';
  public static GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_COUNTRY: string = 'country';
  public static GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_POSTAL_CODE: string = 'postal_code';
  public static GOOGLE_MAPS_TYPE_ADDRESS: string = 'address';
  public static GOOGLE_MAPS_PLACES_FIELD_ID: string = 'id';
  public static GOOGLE_MAPS_PLACES_FIELD_URL: string = 'url';
  public static GOOGLE_MAPS_PLACES_FIELD_TYPES: string = 'types';
  public static GOOGLE_MAPS_PLACES_FIELD_GEOMETRY: string = 'geometry';
  public static GOOGLE_MAPS_PLACES_FIELD_PLACE_ID: string = 'place_id';
  public static GOOGLE_MAPS_PLACES_FIELD_ADDRESS_COMPONENTS: string = 'address_components';
  public static GOOGLE_MAPS_GEOMETRY_FIELD_LOCATION: string = 'location';
  public static GOOGLE_MAPS_LOCATION_FIELD_LATITUDE: string = 'lat';
  public static GOOGLE_MAPS_LOCATION_FIELD_LONGITUDE: string = 'lng';
  public static GOOGLE_MAPS_ADDRESS_COMPONENTS_FIELD_LONG_NAME: string = 'long_name';
  public static GOOGLE_MAPS_ADDRESS_COMPONENTS_FIELD_SHORT_NAME: string = 'short_name';
  public static GOOGLE_MAPS_CLASSNAME_ELEMENT_DROPDOWN: string = 'pac-container';
  public static GOOGLE_MAPS_PARAM_LOADING: string = 'loading';
  public static GOOGLE_MAPS_LOADING_ASYNC: string = 'async';

  private static STATIC_FOLDER_IMAGES_MARKERS: string = '/images/markers';

  private static client: GoogleMapsClient | null = null;

  public static drawMap(
    element: HTMLElement | null,
    center: GoogleMapsCoordinates,
    zoom: number,
    markers: GoogleMapsMarkerInput[],
    isFixedZoom: boolean,
  ): Promise<void> | null {
    if (!element) {
      return null;
    }

    if (!MainService.GOOGLE_MAPS_API_MAP_ID) {
      return null;
    }

    const client: GoogleMapsClient | null = this.getClient();
    if (!client) {
      return null;
    }

    return client.load().then((google: GoogleInstance): void => {
      const map: GoogleMapsMap = new google.maps.Map(element, {
        mapId: MainService.GOOGLE_MAPS_API_MAP_ID,
        center,
        zoom,
        draggable: true,
      } as GoogleMapsMapOptions);

      const markerPositionList: Array<GoogleMapsCoordinates> = [];
      if (markers) {
        markers.forEach((marker: GoogleMapsMarkerInput): void => {
          const markerImage: HTMLImageElement = ScreenService.createElement(HTML_TAG_NAME_IMG) as HTMLImageElement;
          markerImage.src = `${this.STATIC_FOLDER_IMAGES_MARKERS}/${marker.color}.${EXTENSION_FORMAT_PNG}`;

          const markerItem: GoogleMapsMarker = new google.maps.marker.AdvancedMarkerElement({
            map,
            draggable: false,
            position: marker.position,
            content: markerImage,
          });

          if (marker.content) {
            const infoWindow: GoogleMapsInfoWindow = new google.maps.InfoWindow({
              content: marker.content,
            });

            markerItem.addListener('gmp-click', (): void => {
              infoWindow.open(map, markerItem);
            });
          }

          if (marker.position) {
            markerPositionList.push(marker.position);
          }
        });
      }

      const bounds: GoogleMapsCoordinateBounds = new google.maps.LatLngBounds();

      markerPositionList.forEach((markerPosition: GoogleMapsCoordinates): void => {
        bounds.extend(markerPosition);
      });

      if (!isFixedZoom) {
        map.fitBounds(bounds);
      }
    }).catch((exception: ServerError): void => {
      console.error(exception);
    });
  }

  public static addressAutocomplete(
    element: HTMLInputElement | null,
    onAddressSelect: (searchResponse: GoogleMapsPlacesResults) => void,
  ): Promise<void> | null {
    if (!element) {
      return null;
    }

    const client: GoogleMapsClient | null = this.getClient();
    if (!client) {
      return null;
    }

    return client.load().then((google: GoogleInstance): void => {
      const autocomplete: GoogleMapsAutocomplete = new google.maps.places.Autocomplete(element, {
        fields: [
          this.GOOGLE_MAPS_PLACES_FIELD_ID,
          this.GOOGLE_MAPS_PLACES_FIELD_URL,
          this.GOOGLE_MAPS_PLACES_FIELD_TYPES,
          this.GOOGLE_MAPS_PLACES_FIELD_GEOMETRY,
          this.GOOGLE_MAPS_PLACES_FIELD_PLACE_ID,
          this.GOOGLE_MAPS_PLACES_FIELD_ADDRESS_COMPONENTS,
        ],
        types: [this.GOOGLE_MAPS_TYPE_ADDRESS],
      });

      google.maps.event.addListener(autocomplete, 'place_changed', (): void => {
        const searchResponse: GoogleMapsPlacesResults = autocomplete.getPlace();

        if (onAddressSelect) {
          onAddressSelect(searchResponse);
        }
      });
    }).catch((exception: ServerError): void => {
      console.error(exception);
    });
  }

  public static removeAllListeners(inputElement: HTMLElement | null): Promise<void> | null {
    if (!inputElement) {
      return null;
    }

    const client: GoogleMapsClient | null = this.getClient();
    if (!client) {
      return null;
    }

    return client.load().then((google: GoogleInstance): void => {
      const dropdownElements: HTMLCollectionOf<HTMLElement> = ScreenService.getElementsByClassName(
        this.GOOGLE_MAPS_CLASSNAME_ELEMENT_DROPDOWN,
      );

      if (dropdownElements && dropdownElements.length) {
        const dropdownElementItems: Array<HTMLElement> = [...dropdownElements];

        dropdownElementItems.forEach((dropdownElement: HTMLElement): void => {
          if ((dropdownElement as HTMLElement).style.display !== 'none') {
            dropdownElement.remove();
          }
        });
      }

      google.maps.event.clearInstanceListeners(inputElement);
    }).catch((exception: ServerError): void => {
      console.error(exception);
    });
  }

  public static checkIsValidAddress(addressObject: GoogleMapsPlacesResults | null): boolean {
    return !!addressObject && !!addressObject[this.GOOGLE_MAPS_PLACES_FIELD_URL]
      && !!addressObject[this.GOOGLE_MAPS_PLACES_FIELD_ADDRESS_COMPONENTS];
  }

  public static getFullAddress(addressObject: GoogleMapsPlacesResults | null): string {
    const postalCode: string = this.extractPostalCode(addressObject);

    return `${this.extractStreetAddress(addressObject)}, ${this.extractLocation(addressObject)}`
      + `${postalCode ? `, ${postalCode}` : ''}`;
  }

  public static extractLatitude(addressObject: GoogleMapsPlacesResults | null): number | null {
    return this.extractGeometryLocationElement(addressObject, this.GOOGLE_MAPS_LOCATION_FIELD_LATITUDE);
  }

  public static extractLongitude(addressObject: GoogleMapsPlacesResults | null): number | null {
    return this.extractGeometryLocationElement(addressObject, this.GOOGLE_MAPS_LOCATION_FIELD_LONGITUDE);
  }

  public static extractStreetAddress(addressObject: GoogleMapsPlacesResults | null): string {
    if (!addressObject) {
      return '';
    }

    const house: string = this.extractAddressComponent(addressObject, this.GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_HOUSE);
    const street: string = this.extractAddressComponent(
      addressObject, this.GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_STREET, true,
    );

    return `${house} ${street}`;
  }

  public static extractLocation(addressObject: GoogleMapsPlacesResults | null): string {
    if (!addressObject) {
      return '';
    }

    let city: string = this.extractAddressComponent(addressObject, this.GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_LOCALITY);
    if (!city) {
      city = this.extractAddressComponent(addressObject, this.GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_SUBLOCALITY);
    }
    const state: string = this.extractAddressComponent(addressObject, this.GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_STATE);
    const country: string = this.extractAddressComponent(
      addressObject, this.GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_COUNTRY, true,
    );

    return city ? `${city}, ${state}` : `${state}, ${country}`;
  }

  public static extractPostalCode(addressObject: GoogleMapsPlacesResults | null): string {
    if (!addressObject) {
      return '';
    }

    return this.extractAddressComponent(addressObject, this.GOOGLE_MAPS_ADDRESS_COMPONENT_NAME_POSTAL_CODE);
  }

  public static extractAddressComponent(
    addressObject?: GoogleMapsPlacesResults,
    componentName?: string,
    useLongValue?: boolean,
  ): string {
    if (!addressObject) {
      return '';
    }

    const addressComponents: GoogleMapsAddressComponent[] | undefined = (
      addressObject[this.GOOGLE_MAPS_PLACES_FIELD_ADDRESS_COMPONENTS]
    );
    if (addressComponents) {
      for (let addressComponentIndex: number = 0; addressComponentIndex < addressComponents.length;
           addressComponentIndex++) {
        const addressComponent: GoogleMapsAddressComponent = addressComponents[addressComponentIndex];
        const addressComponentTypes: string[] = addressComponent.types;

        if (componentName && addressComponentTypes.includes(componentName)) {
          return useLongValue ? addressComponent[this.GOOGLE_MAPS_ADDRESS_COMPONENTS_FIELD_LONG_NAME]
            : addressComponent[this.GOOGLE_MAPS_ADDRESS_COMPONENTS_FIELD_SHORT_NAME];
        }
      }
    }

    return '';
  }

  private static extractGeometryLocationElement(
    addressObject: GoogleMapsPlacesResults,
    geometryLocationElementName: string,
  ): number | null {
    if (!addressObject) {
      return null;
    }

    const geometryObject: GoogleMapsGeometry = addressObject[this.GOOGLE_MAPS_PLACES_FIELD_GEOMETRY];
    const geometryLocation: GoogleMapsGeometryLocation = geometryObject
      ? geometryObject[this.GOOGLE_MAPS_GEOMETRY_FIELD_LOCATION] : null;

    return geometryLocation ? geometryLocation[geometryLocationElementName] : null;
  }

  private static getClient(): GoogleMapsClient | null {
    if (this.client) {
      return this.client;
    }

    const googleMapsApiKey: string | undefined = MainService.GOOGLE_MAPS_API_KEY;
    const googleMapsApiVersion: string | undefined = MainService.GOOGLE_MAPS_API_VERSION;

    this.client = googleMapsApiKey && googleMapsApiVersion ? new Loader(
      `${googleMapsApiKey}&${this.GOOGLE_MAPS_PARAM_LOADING}=${this.GOOGLE_MAPS_LOADING_ASYNC}`,
      {
        version: googleMapsApiVersion,
        libraries: [this.GOOGLE_MAPS_LIBRARY_PLACES, this.GOOGLE_MAPS_LIBRARY_MARKER],
      },
    ) : null;

    return this.client;
  }
}
